import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { horseAPI } from '../../../services/api';

const HorsePedigreeCard = ({ horse, canEditAccount, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const startEditing = () => {
    setForm({
      sire_name: horse.sire_name || '',
      dam_name: horse.dam_name || '',
      dam_year_foaled: horse.dam_year_foaled || '',
      sire_grandsire_name: horse.sire_grandsire_name || '',
      sire_granddam_name: horse.sire_granddam_name || '',
      sire_granddam_year_foaled: horse.sire_granddam_year_foaled || '',
      dam_grandsire_name: horse.dam_grandsire_name || '',
      dam_granddam_name: horse.dam_granddam_name || '',
      dam_granddam_year_foaled: horse.dam_granddam_year_foaled || '',
      pedigree_reference_link: horse.pedigree_reference_link || '',
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await horseAPI.update(horse.id, form);
      toast.success('Pedigree updated');
      setIsEditing(false);
      onUpdate?.();
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const f = (key) => form[key] ?? '';
  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base">Pedigree</CardTitle>
        {canEditAccount && !isEditing && (
          <Button variant="ghost" size="icon" onClick={startEditing} data-testid="edit-pedigree-btn">
            <Pencil className="w-4 h-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-4 overflow-hidden">
        <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="min-w-[400px]">
            {/* Generation Labels */}
            <div className="grid grid-cols-3 gap-1.5 mb-1.5 text-[10px] text-muted-foreground font-medium">
              <div className="text-center">Horse</div>
              <div className="text-center">Parents</div>
              <div className="text-center">Grandparents</div>
            </div>

            <div className="grid grid-cols-3 gap-1.5">
              {/* Horse */}
              <div className="flex items-center">
                <div className="p-2 border bg-primary/10 border-primary w-full rounded text-center">
                  <p className="font-medium text-xs text-primary truncate">{horse.system_name || horse.name}</p>
                  {horse.birthdate && (
                    <p className="text-[10px] text-muted-foreground">
                      ({new Date(`${horse.birthdate.split('T')[0]}T12:00:00`).getFullYear()})
                    </p>
                  )}
                </div>
              </div>

              {/* Parents */}
              <div className="flex flex-col justify-center gap-1">
                <div className="p-1.5 border bg-blue-50 border-blue-200 w-full rounded">
                  {isEditing ? (
                    <Input value={f('sire_name')} onChange={e => set('sire_name', e.target.value)} placeholder="Sire name" className="h-6 text-xs" />
                  ) : (
                    <><p className="font-medium text-xs truncate">{horse.sire_name || 'Unknown'}</p><p className="text-[10px] text-muted-foreground">Sire</p></>
                  )}
                </div>
                <div className="p-1.5 border bg-pink-50 border-pink-200 w-full rounded">
                  {isEditing ? (
                    <div className="space-y-0.5">
                      <Input value={f('dam_name')} onChange={e => set('dam_name', e.target.value)} placeholder="Dam name" className="h-6 text-xs" />
                      <Input type="number" value={f('dam_year_foaled')} onChange={e => set('dam_year_foaled', e.target.value)} placeholder="Year" className="h-5 text-[10px]" />
                    </div>
                  ) : (
                    <><p className="font-medium text-xs truncate">{horse.dam_name || 'Unknown'}</p><p className="text-[10px] text-muted-foreground">Dam {horse.dam_year_foaled ? `(${horse.dam_year_foaled})` : ''}</p></>
                  )}
                </div>
              </div>

              {/* Grandparents */}
              <div className="flex flex-col justify-center gap-0.5">
                {[
                  { key: 'sire_grandsire_name', bg: 'blue', placeholder: "Grandsire", display: horse.sire_grandsire_name },
                  { key: 'sire_granddam_name', bg: 'blue', placeholder: "Granddam", display: horse.sire_granddam_name, year: horse.sire_granddam_year_foaled },
                  { key: 'dam_grandsire_name', bg: 'pink', placeholder: "Grandsire", display: horse.dam_grandsire_name },
                  { key: 'dam_granddam_name', bg: 'pink', placeholder: "Granddam", display: horse.dam_granddam_name, year: horse.dam_granddam_year_foaled },
                ].map(({ key, bg, placeholder, display, year }) => (
                  <div key={key} className={`p-1 border bg-${bg}-50 border-${bg}-200 w-full rounded`}>
                    {isEditing ? (
                      <Input value={f(key)} onChange={e => set(key, e.target.value)} placeholder={placeholder} className="h-5 text-[10px]" />
                    ) : (
                      <p className="text-[10px] truncate">{display || 'Unknown'}{year ? ` (${year})` : ''}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Pedigree Reference Link */}
        {(isEditing || horse.pedigree_reference_link) && (
          <div className="mt-3 pt-3 border-t">
            {isEditing ? (
              <div className="space-y-1">
                <label className="text-xs font-medium">Pedigree Reference Link</label>
                <Input value={f('pedigree_reference_link')} onChange={e => set('pedigree_reference_link', e.target.value)} placeholder="URL to full pedigree (optional)" type="url" className="h-7 text-xs" />
              </div>
            ) : horse.pedigree_reference_link ? (
              <a href={horse.pedigree_reference_link} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                View Full Pedigree →
              </a>
            ) : null}
          </div>
        )}

        {isEditing && (
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" size="sm" onClick={() => setIsEditing(false)} disabled={saving}>Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HorsePedigreeCard;
